import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import {
  FIRESTORE_EXACT_HISTORY_TABLE,
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE,
  FIRESTORE_PURCHASE_ORDERS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { addLogEntry } from "../../../../../utils/logging";
import { formatPriceByCurrency } from "../../../../../functions/formatters";
import urls from "../../../../../urls";
import { useSelector } from "react-redux";
import {
  usePurchaseOrderInvoices,
  usePurchaseOrderPayments,
  usePurchaseOrders,
  useProducts,
  useSuppliers,
} from "../../../../../data/hooks";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";

const { Panel } = Collapse;
const { TextArea } = Input;

const statusColors = {
  draft: "default",
  shipped: "blue",
  in_transit: "orange",
  received: "green",
  completed: "cyan",
  cancelled: "red",
};

const statusLabels = {
  draft: "Concept",
  shipped: "Verzonden",
  in_transit: "Onderweg",
  received: "Ontvangen",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderShipmentDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [shipment, setShipment] = useState(null);
  const [shipmentInvoiceId, setShipmentInvoiceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentExchangeRate, setPaymentExchangeRate] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showGoodsReceiptModal, setShowGoodsReceiptModal] = useState(false);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [receiptDate, setReceiptDate] = useState(dayjs());
  const [processingReceipt, setProcessingReceipt] = useState(false);
  const [loadingCostPrices, setLoadingCostPrices] = useState(false);
  const [sendingAccountantMail, setSendingAccountantMail] = useState(false);
  const [editingShipment, setEditingShipment] = useState(false);
  const [editedShipmentItems, setEditedShipmentItems] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editedInvoiceLines, setEditedInvoiceLines] = useState([]);
  const [editedAdditionalCosts, setEditedAdditionalCosts] = useState([]);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newLineQuantity, setNewLineQuantity] = useState(1);
  const [newLineUnitPrice, setNewLineUnitPrice] = useState(0);
  const [newLineDiscount, setNewLineDiscount] = useState(0);
  const [showAddShipmentItemModal, setShowAddShipmentItemModal] = useState(false);
  const [selectedShipmentProduct, setSelectedShipmentProduct] = useState(null);
  const [newShipmentItemQuantity, setNewShipmentItemQuantity] = useState(1);
  const [newShipmentItemUnitPrice, setNewShipmentItemUnitPrice] = useState(0);
  const [showRetryExactModal, setShowRetryExactModal] = useState(false);
  const [retryingExactSync, setRetryingExactSync] = useState(false);
  const [goodsReceiptIsRetry, setGoodsReceiptIsRetry] = useState(false);
  const [showCostPriceModal, setShowCostPriceModal] = useState(false);
  const [costPriceLines, setCostPriceLines] = useState([]);
  const [costPriceExchangeRate, setCostPriceExchangeRate] = useState(1);
  const [updatingCostPrices, setUpdatingCostPrices] = useState(false);
  const [costPricesAlreadyUpdated, setCostPricesAlreadyUpdated] = useState(false);
  const [costPriceRetryMode, setCostPriceRetryMode] = useState(false);
  const [shipmentCurrency, setShipmentCurrency] = useState("USD");
  const [costPriceHistory, setCostPriceHistory] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [legacyPayments, setLegacyPayments] = useState([]);
  const [editingContainerNumber, setEditingContainerNumber] = useState(false);
  const [containerNumberValue, setContainerNumberValue] = useState("");
  const [savingContainerNumber, setSavingContainerNumber] = useState(false);
  const [containerShipments, setContainerShipments] = useState([]);
  const [showPreOrderModal, setShowPreOrderModal] = useState(false);
  const [preOrderDate, setPreOrderDate] = useState(null);
  const [preOrderShipmentIds, setPreOrderShipmentIds] = useState([]);
  const [savingPreOrder, setSavingPreOrder] = useState(false);
  const email = useSelector((state) => state.firebaseReducer.auth.email);

  // Use hooks for invoices and payments
  const { invoices } = usePurchaseOrderInvoices({ shipmentId: id });
  const invoice = invoices.length > 0 ? invoices[0] : null;
  const { payments } = usePurchaseOrderPayments({ invoiceId: invoice?.id });
  const { purchaseOrders } = usePurchaseOrders();
  const { suppliers } = useSuppliers();

  // Get current supplier to extract productIds
  const currentSupplier = suppliers.find((s) => s.uid === shipment?.supplierId);
  const { products } = useProducts({ productIds: currentSupplier?.productIds || [] });

  // Load legacy payments from purchase orders using hook data
  useEffect(() => {
    if (!shipment?.sourceOrders || !purchaseOrders?.length) return;

    const allLegacyPayments = [];
    const orderIds = shipment.sourceOrders.map((so) => so.purchaseOrderId).filter(Boolean);

    for (const orderId of orderIds) {
      const order = purchaseOrders.find((po) => po.id === orderId);
      if (order && order.payments && Array.isArray(order.payments)) {
        for (const payment of order.payments) {
          allLegacyPayments.push({
            ...payment,
            sourceOrderNumber: order.orderNumber,
            sourceOrderId: orderId,
            isLegacy: true,
          });
        }
      }
    }

    setLegacyPayments(allLegacyPayments);
  }, [shipment, purchaseOrders]);

  // Combine legacy payments with invoice payments (legacy first)
  const allPayments = [
    ...legacyPayments.map((p) => ({ ...p, id: `legacy-${p.sourceOrderId}-${p.paidAt}` })),
    ...(payments || []),
  ];

  console.log(allPayments);
  console.log(costPriceHistory);

  const loadCostPriceHistory = async () => {
    if (!id) return;
    try {
      const snapshot = await firestore
        .collection(FIRESTORE_EXACT_HISTORY_TABLE)
        .where("sourceId", "==", id)
        .where("operation", "==", "cost_price_update")
        .where("canRollback", "==", true)
        .orderBy("performedAt", "desc")
        .limit(1)
        .get();
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const historyData = { id: doc.id, ...doc.data() };
        // const hoursSinceUpdate = (dayjs().unix() - historyData.performedAt) / 3600;
        // if (hoursSinceUpdate < 24) {
        setCostPriceHistory(historyData);
        // }
      }
    } catch (e) {
      console.warn("Kon kostprijs history niet laden:", e);
    }
  };

  const loadContainerShipments = async (containerNum) => {
    if (!containerNum) {
      setContainerShipments([]);
      return;
    }
    try {
      const snap = await firestore
        .collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE)
        .where("containerNumber", "==", containerNum)
        .get();
      const others = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.id !== id);
      setContainerShipments(others);
    } catch (e) {
      console.warn("Kon medeverzendingen niet laden:", e);
    }
  };

  const loadShipment = async () => {
    try {
      const doc = await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).get();
      if (doc.exists) {
        const shipmentData = { id: doc.id, ...doc.data() };
        setShipment(shipmentData);
        setShipmentInvoiceId(shipmentData.invoiceId || null);
        setContainerNumberValue(shipmentData.containerNumber || "");
        await loadContainerShipments(shipmentData.containerNumber || null);

        // Load currency from first linked purchase order
        const firstOrderId = shipmentData.sourceOrders?.[0]?.purchaseOrderId;
        if (firstOrderId) {
          try {
            const poDoc = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(firstOrderId).get();
            if (poDoc.exists) {
              setShipmentCurrency((poDoc.data().currency || "USD").toUpperCase());
            }
          } catch (e) {
            console.warn("Kon valuta niet ophalen:", e);
          }
        }
      } else {
        message.error(t("shipment.not_found"));
      }
    } catch (error) {
      console.error("Error loading shipment:", error);
      message.error(t("shipment.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadShipment();
      loadCostPriceHistory();
    }
  }, [id]);

  const handleSaveContainerNumber = async () => {
    try {
      setSavingContainerNumber(true);
      const trimmed = containerNumberValue.trim() || null;
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "shipment_updated",
        user: email,
        metadata: { field: "containerNumber", newValue: trimmed },
      });
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        containerNumber: trimmed,
        log: newLog,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("shipment.updated_success"));
      setEditingContainerNumber(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving container number:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSavingContainerNumber(false);
    }
  };

  const handleOpenPreOrderModal = () => {
    setPreOrderDate(null);
    setPreOrderShipmentIds([id, ...containerShipments.map((s) => s.id)]);
    setShowPreOrderModal(true);
  };

  const handleSavePreOrder = async () => {
    if (!preOrderDate) {
      message.error("Selecteer een aankomstdatum");
      return;
    }
    const dateStr = preOrderDate.format("DD-MM-YYYY");
    try {
      setSavingPreOrder(true);

      const allShipmentsToUpdate = [shipment, ...containerShipments].filter((s) => preOrderShipmentIds.includes(s.id));

      let totalUpdatedProducts = 0;

      for (const sh of allShipmentsToUpdate) {
        const productIds = (sh.items || []).map((item) => item.productId).filter(Boolean);
        const uniqueProductIds = [...new Set(productIds)];

        for (const productId of uniqueProductIds) {
          await firestore
            .collection("products")
            .doc(productId)
            .update({
              preOrder: { active: true, availableAt: dateStr },
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
          totalUpdatedProducts++;
        }

        await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(sh.id).update({
          preOrderDatesUpdated: true,
          preOrderDatesUpdatedAt: dayjs().unix(),
          preOrderAvailableAt: dateStr,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
      }

      message.success(`Pre-order datum ingesteld op ${dateStr} voor ${totalUpdatedProducts} producten`);
      setShowPreOrderModal(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving pre-order dates:", error);
      message.error("Fout bij instellen pre-order datums");
    } finally {
      setSavingPreOrder(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      setSaving(true);
      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: shipment.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
      };

      if (newStatus === "received") {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update(updateData);
      message.success(t("shipment.updated_success"));
      await loadShipment();
    } catch (error) {
      console.error("Error updating shipment status:", error);
      message.error(t("shipment.update_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInvoiceStatus = async (newStatus) => {
    if (!invoice) return;
    try {
      setSaving(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success("Factuurstatus bijgewerkt");
      await loadShipment();
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error("Fout bij bijwerken factuurstatus");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditingShipment = () => {
    setEditedShipmentItems(JSON.parse(JSON.stringify(shipment.items || [])));
    setEditingShipment(true);
  };

  const handleCancelEditingShipment = () => {
    setEditingShipment(false);
    setEditedShipmentItems([]);
  };

  const handleShipmentItemChange = (index, field, value) => {
    const updated = [...editedShipmentItems];
    updated[index] = { ...updated[index], [field]: value };
    setEditedShipmentItems(updated);
  };

  const handleRemoveShipmentItem = (index) => {
    setEditedShipmentItems(editedShipmentItems.filter((_, i) => i !== index));
    message.success("Product verwijderd uit zending");
  };

  const handleOpenAddShipmentItemModal = () => {
    setSelectedShipmentProduct(null);
    setNewShipmentItemQuantity(1);
    setNewShipmentItemUnitPrice(0);
    setShowAddShipmentItemModal(true);
  };

  const handleCloseAddShipmentItemModal = () => {
    setShowAddShipmentItemModal(false);
    setSelectedShipmentProduct(null);
    setNewShipmentItemQuantity(1);
    setNewShipmentItemUnitPrice(0);
  };

  const handleShipmentProductSelect = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedShipmentProduct(product);
      setNewShipmentItemUnitPrice(product.purchasePrice || 0);
    }
  };

  const handleAddShipmentItem = () => {
    if (!selectedShipmentProduct) {
      message.error("Selecteer een product");
      return;
    }
    if (!newShipmentItemQuantity || newShipmentItemQuantity <= 0) {
      message.error("Vul een geldige hoeveelheid in");
      return;
    }
    if (!newShipmentItemUnitPrice || newShipmentItemUnitPrice <= 0) {
      message.error("Vul een geldige prijs in");
      return;
    }

    const newItem = {
      productId: selectedShipmentProduct.id,
      productCode: selectedShipmentProduct.code || "",
      productName: selectedShipmentProduct.name,
      productColor: selectedShipmentProduct.color || null,
      quantity: newShipmentItemQuantity,
      unitPrice: newShipmentItemUnitPrice,
    };

    setEditedShipmentItems([...editedShipmentItems, newItem]);
    handleCloseAddShipmentItemModal();
    message.success("Product toegevoegd aan zending");
  };

  const handleSaveShipmentEdits = async () => {
    try {
      setSaving(true);

      // Recalculate totals
      const totalProducts = editedShipmentItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const totalPrice = editedShipmentItems.reduce(
        (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
        0,
      );

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({
        items: editedShipmentItems,
        totalProducts,
        totalPrice,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Verzending bijgewerkt");
      setEditingShipment(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving shipment edits:", error);
      message.error("Fout bij opslaan wijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditingInvoice = () => {
    setEditedInvoiceLines(JSON.parse(JSON.stringify(invoice.lines || [])));
    setEditedAdditionalCosts(JSON.parse(JSON.stringify(invoice.additionalCosts || [])));
    setEditingInvoice(true);
  };

  const handleCancelEditingInvoice = () => {
    setEditingInvoice(false);
    setEditedInvoiceLines([]);
    setEditedAdditionalCosts([]);
  };

  const handleAddInvoiceLine = () => {
    if (!selectedProduct) {
      message.error("Selecteer een product");
      return;
    }
    if (!newLineQuantity || newLineQuantity <= 0) {
      message.error("Vul een geldige hoeveelheid in");
      return;
    }
    if (!newLineUnitPrice || newLineUnitPrice <= 0) {
      message.error("Vul een geldige prijs in");
      return;
    }

    const totalPrice = newLineQuantity * newLineUnitPrice * (1 - newLineDiscount / 100);
    const newLine = {
      productId: selectedProduct.id,
      productCode: selectedProduct.sku || selectedProduct.code || "",
      productName: selectedProduct.name,
      productColor: selectedProduct.color || null,
      quantity: newLineQuantity,
      unitPrice: newLineUnitPrice,
      discountPercentage: newLineDiscount,
      totalPrice: totalPrice,
    };

    setEditedInvoiceLines([...editedInvoiceLines, newLine]);
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    message.success("Factuurregel toegevoegd");
  };

  const handleRemoveInvoiceLine = (index) => {
    const updatedLines = editedInvoiceLines.filter((_, i) => i !== index);
    setEditedInvoiceLines(updatedLines);
    message.success("Factuurregel verwijderd");
  };

  const handleOpenAddLineModal = () => {
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    setShowAddLineModal(true);
  };

  const handleCloseAddLineModal = () => {
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
  };

  const handleProductSelect = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setNewLineUnitPrice(product.purchasePrice || 0);
    }
  };

  const handleInvoiceLineChange = (index, field, value) => {
    const updated = [...editedInvoiceLines];
    const line = { ...updated[index] };

    if (field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
      line[field] = value;
      // Recalculate totalPrice for this line
      const qty = field === "quantity" ? value : line.quantity || 0;
      const price = field === "unitPrice" ? value : line.unitPrice || 0;
      const discount = field === "discountPercentage" ? value : line.discountPercentage || 0;
      line.totalPrice = qty * price * (1 - discount / 100);
    } else {
      line[field] = value;
    }

    updated[index] = line;
    setEditedInvoiceLines(updated);
  };

  const handleAdditionalCostChange = (index, field, value) => {
    const updated = [...editedAdditionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAdditionalCosts(updated);
  };

  const handleSaveInvoiceEdits = async () => {
    try {
      setSaving(true);

      // Recalculate totals
      const subtotalProducts = editedInvoiceLines.reduce((sum, line) => sum + (line.totalPrice || 0), 0);
      const subtotalAdditionalCosts = editedAdditionalCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      const totalAmount = subtotalProducts + subtotalAdditionalCosts;
      const currentTotalPaid = invoice.totalPaid || 0;
      const amountDue = totalAmount - currentTotalPaid;

      // Determine status based on payment
      let status = invoice.status;
      if (currentTotalPaid >= totalAmount) {
        status = "paid";
      } else if (currentTotalPaid > 0) {
        status = "partially_paid";
      }

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
        .doc(invoice.id)
        .update({
          lines: editedInvoiceLines,
          additionalCosts: editedAdditionalCosts.length > 0 ? editedAdditionalCosts : null,
          subtotalProducts,
          subtotalAdditionalCosts,
          totalAmount,
          amountDue: amountDue > 0 ? amountDue : 0,
          status,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });

      message.success("Factuur bijgewerkt");
      setEditingInvoice(false);
      await loadShipment();
    } catch (error) {
      console.error("Error saving invoice edits:", error);
      message.error("Fout bij opslaan wijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount(null);
    setPaymentDate(dayjs());
    setPaymentReference("");
    setPaymentNotes("");
    setPaymentProofFile(null);
    setPaymentExchangeRate(null);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentAmount(payment.amount);
    setPaymentDate(payment.paymentDate ? dayjs.unix(payment.paymentDate) : dayjs());
    setPaymentReference(payment.reference || "");
    setPaymentNotes(payment.notes || "");
    setPaymentExchangeRate(payment.exchangeRate || null);
    setPaymentProofFile(null); // Reset file, will show existing proof in modal
    setShowEditPaymentModal(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !paymentAmount || paymentAmount <= 0) {
      message.error("Vul een geldig bedrag in");
      return;
    }
    if (!paymentExchangeRate || paymentExchangeRate <= 0) {
      message.error("Vul een geldige wisselkoers in");
      return;
    }
    if (!paymentDate) {
      message.error("Selecteer een betaaldatum");
      return;
    }

    try {
      setSavingPayment(true);

      let proofUrl = editingPayment.proofUrl;
      if (paymentProofFile) {
        // Upload new proof if provided
        const storage = getStorage();
        const fileName = `payments/${invoice.id}/${Date.now()}_${paymentProofFile.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, paymentProofFile);
        proofUrl = await getDownloadURL(storageRef);
      }

      const updatedData = {
        amount: paymentAmount,
        currency: shipmentCurrency,
        exchangeRate: paymentExchangeRate,
        amountEur: paymentAmount * paymentExchangeRate,
        paymentDate: paymentDate.unix(),
        reference: paymentReference.trim() || null,
        notes: paymentNotes.trim() || null,
        proofUrl,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).doc(editingPayment.id).update(updatedData);

      // Recalculate invoice totals
      const allPaymentsSnapshot = await firestore
        .collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE)
        .where("invoiceId", "==", invoice.id)
        .get();

      const allPaymentsData = allPaymentsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const newTotalPaid = allPaymentsData.reduce((sum, p) => sum + (p.amount || 0), 0);
      const newAmountDue = (invoice.totalAmount || 0) - newTotalPaid;
      const newStatus =
        newTotalPaid >= (invoice.totalAmount || 0)
          ? "paid"
          : newTotalPaid > 0
            ? "partially_paid"
            : invoice.status || "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
      });

      message.success("Betaling bijgewerkt");
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      resetPaymentForm();
      await loadShipment();
    } catch (error) {
      console.error("Error updating payment:", error);
      message.error("Fout bij bijwerken betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleOpenGoodsReceiptModal = async () => {
    const initialQuantities = {};
    (shipment.items || []).forEach((item, index) => {
      initialQuantities[index] = item.quantity;
    });
    setReceivedQuantities(initialQuantities);
    setReceiptDate(dayjs());

    // Check if we need to show cost price modal first
    const firstOrder = shipment.sourceOrders?.[0];
    let exchangeRate = 1;
    let supplierGuid = null;

    try {
      // Calculate weighted exchange rate based on all payments across related purchase orders
      let totalAmount = 0;
      let weightedRateSum = 0;

      for (const sourceOrder of shipment.sourceOrders || []) {
        if (!sourceOrder.purchaseOrderId) continue;

        const orderPayments = allPayments.filter(
          (p) => p.shipmentId === shipment.id || p.sourceOrderId === sourceOrder.purchaseOrderId,
        );

        for (const payment of orderPayments) {
          const amount = Number(payment.amount) || 0;
          totalAmount += amount;
          weightedRateSum += amount * (payment.exchangeRate || 1);
        }
      }

      if (totalAmount > 0) {
        exchangeRate = weightedRateSum / totalAmount;
      }

      // Get supplier info
      const purchaseOrder = purchaseOrders.find((po) => po.id === firstOrder.purchaseOrderId);
      if (purchaseOrder && purchaseOrder.supplier) {
        const supplier = suppliers.find((s) => s.id === purchaseOrder.supplier);
        if (supplier && supplier.exactGuid) {
          supplierGuid = supplier.exactGuid;
        }
      }

      // Check if any items have foreign currency
      const hasForeignCurrency = (shipment.items || []).some((item) => item.currency && item.currency !== "EUR");

      if (hasForeignCurrency && supplierGuid) {
        setLoadingCostPrices(true);
        const idToken = await getFirebaseIDToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/items/current-cost-prices`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: idToken },
          body: JSON.stringify({
            itemCodes: (shipment.items || []).map((item) => item.productCode).filter(Boolean),
            supplierGuid,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const costPriceLines = (shipment.items || []).map((item) => {
            const existingCostPrice = data.find((cp) => cp.itemCode === item.productCode);
            return {
              productCode: item.productCode,
              productName: item.productName,
              originalCostPrice: item.purchasePrice || 0,
              currentCostPrice: existingCostPrice?.costPrice || 0,
              newCostPriceEur: (item.purchasePrice || 0) * exchangeRate,
              supplierGuid,
            };
          });
          setCostPriceLines(costPriceLines);
          setCostPriceExchangeRate(exchangeRate);
          setShowCostPriceModal(true);
          return; // Don't open goods receipt modal until cost prices are handled
        }
      }
    } catch (error) {
      console.warn("Could not check cost prices:", error);
    } finally {
      setLoadingCostPrices(false);
    }

    setGoodsReceiptIsRetry(false);
    setShowGoodsReceiptModal(true);
  };

  const handleResendAccountantMail = async () => {
    try {
      setSendingAccountantMail(true);
      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/shipments/${id}/notify-accountant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
      });

      if (response.ok) {
        const accountantMailSentAt = dayjs().unix();
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({ accountantMailSentAt });
        message.success("Mail naar boekhouder opnieuw verstuurd");
        await loadShipment(); // Refresh to show the new timestamp
      } else {
        throw new Error("Kon mail niet versturen");
      }
    } catch (error) {
      console.error("Error resending accountant mail:", error);
      message.error("Fout bij versturen mail naar boekhouder");
    } finally {
      setSendingAccountantMail(false);
    }
  };

  const handleOpenCostPriceModal = async (retryMode = false) => {
    setCostPriceRetryMode(retryMode);
    const firstOrder = shipment.sourceOrders?.[0];
    let exchangeRate = 1;
    let supplierGuid = null;

    try {
      // Calculate weighted exchange rate based on all payments across related purchase orders
      let totalAmount = 0;
      let weightedRateSum = 0;

      for (const sourceOrder of shipment.sourceOrders || []) {
        if (!sourceOrder.purchaseOrderId) continue;

        // Get all payments for this shipment (including legacy payments)
        const orderPayments = allPayments.filter(
          (p) => p.shipmentId === shipment.id || p.sourceOrderId === sourceOrder.purchaseOrderId,
        );

        for (const payment of orderPayments) {
          const amount = Number(payment.amount) || 0;
          const rate = Number(payment.exchangeRate) || 1;

          totalAmount += amount;
          weightedRateSum += amount * rate;
        }
      }

      // If we have payments, use the weighted average; otherwise fall back to PO rate
      if (totalAmount > 0) {
        exchangeRate = weightedRateSum / totalAmount;
        console.log(`Gewogen wisselkoers berekend: ${exchangeRate} (totaal bedrag: ${totalAmount})`);
      } else if (firstOrder?.purchaseOrderId) {
        // Fallback: use exchange rate from first purchase order
        const poDoc = await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(firstOrder.purchaseOrderId).get();
        if (poDoc.exists) {
          const poData = poDoc.data();
          exchangeRate = poData.exchangeRate ? Number(poData.exchangeRate) : 1;
          console.log(`Fallback wisselkoers van PO: ${exchangeRate}`);
        }
      }

      // Get supplier's Exact GUID
      if (shipment.supplierId) {
        const supplierDoc = await firestore.collection("suppliers").doc(shipment.supplierId).get();
        if (supplierDoc.exists) {
          const supplierData = supplierDoc.data();
          supplierGuid = supplierData.exactGuid || null;
          if (!supplierGuid) {
            message.warning("Leverancier heeft geen Exact koppeling. Kostprijzen kunnen niet bijgewerkt worden.");
            return;
          }
        }
      } else {
        message.error("Geen leverancier gekoppeld aan deze verzending.");
        return;
      }
    } catch (e) {
      console.warn("Kon wisselkoers of leverancier niet ophalen:", e);
      message.error("Fout bij ophalen gegevens: " + e.message);
      return;
    }

    const lines = (shipment.items || []).map((item) => ({
      itemCode: item.productCode,
      productName: item.productName,
      unitPriceOriginal: item.unitPrice || 0,
      exchangeRate,
      newCostPriceEur: Math.round((item.unitPrice || 0) * exchangeRate * 100) / 100,
      supplierGuid,
    }));

    // Check if all cost prices have already been updated in Exact
    let allUpdated = false;

    try {
      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/items/current-cost-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          itemCodes: lines.map((l) => l.itemCode),
          supplierGuid,
        }),
      });

      if (response.ok) {
        const currentCostPrices = await response.json();
        allUpdated = lines.every((line) => {
          const expectedCostPrice = Math.round((line.unitPriceOriginal || 0) * exchangeRate * 100) / 100;
          const currentCostPrice =
            currentCostPrices.find((cp) => cp.itemCode === line.itemCode)?.costPriceStandard || 0;
          return Math.abs(currentCostPrice - expectedCostPrice) < 0.01; // Allow small rounding differences
        });
      } else {
        // If API call fails, fall back to calculation check (less reliable)
        console.warn("Could not fetch current cost prices from Exact, falling back to calculation check");
        allUpdated = lines.every((line) => {
          const expectedCostPrice = Math.round((line.unitPriceOriginal || 0) * exchangeRate * 100) / 100;
          return Math.abs(line.newCostPriceEur - expectedCostPrice) < 0.01;
        });
      }
    } catch (error) {
      console.warn("Error checking current cost prices in Exact:", error);
      // Fall back to calculation check
      allUpdated = lines.every((line) => {
        const expectedCostPrice = Math.round((line.unitPriceOriginal || 0) * exchangeRate * 100) / 100;
        return Math.abs(line.newCostPriceEur - expectedCostPrice) < 0.01;
      });
    }

    setCostPriceExchangeRate(exchangeRate);
    setCostPriceLines(lines);
    setShowCostPriceModal(true);

    setCostPricesAlreadyUpdated(allUpdated);

    if (allUpdated) {
      message.info("Alle kostprijzen zijn al bijgewerkt met de huidige wisselkoers.");
    }
  };

  const handleCostPriceLineChange = (index, field, value) => {
    const updated = [...costPriceLines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "exchangeRate") {
      updated[index].newCostPriceEur = Math.round((updated[index].unitPriceOriginal || 0) * value * 100) / 100;
    }
    setCostPriceLines(updated);
    setCostPricesAlreadyUpdated(false);
  };

  const handleExchangeRateChange = (value) => {
    const rate = value || 1;
    setCostPriceExchangeRate(rate);
    setCostPricesAlreadyUpdated(false);
    setCostPriceLines(
      costPriceLines.map((line) => ({
        ...line,
        exchangeRate: rate,
        newCostPriceEur: Math.round((line.unitPriceOriginal || 0) * rate * 100) / 100,
      })),
    );
  };

  const handleConfirmCostPriceUpdate = async () => {
    try {
      setUpdatingCostPrices(true);
      const idToken = await getFirebaseIDToken();
      const supplierGuid = costPriceLines[0]?.supplierGuid;
      if (!supplierGuid) {
        throw new Error("Geen leverancier GUID beschikbaar");
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/purchase-orders/update-cost-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          exchangeRate: costPriceExchangeRate,
          supplierGuid: supplierGuid,
          lines: costPriceLines.map((l) => ({
            itemCode: l.itemCode,
            unitPriceOriginal: l.unitPriceOriginal,
            newCostPriceEur: l.newCostPriceEur,
          })),
        }),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "API fout bij kostprijsupdate");
      }
      const result = await response.json();

      // Save history record for rollback capability
      const historyRecord = {
        entityType: "item",
        entityId: "bulk_cost_price_update",
        operation: "cost_price_update",
        oldValues: {
          items: result.results.map((r) => ({
            itemCode: r.itemCode,
            itemName: r.itemName,
            costPriceStandard: r.oldCostPriceEur,
          })),
        },
        newValues: {
          exchangeRate: costPriceExchangeRate,
          items: result.results.map((r) => ({
            itemCode: r.itemCode,
            itemName: r.itemName,
            costPriceStandard: r.newCostPriceEur,
          })),
        },
        sourceType: "purchaseOrderShipment",
        sourceId: shipment.id,
        performedBy: email,
        performedAt: dayjs().unix(),
        canRollback: true,
        metadata: {
          shipmentNumber: shipment.shipmentNumber,
          updatedCount: result.updatedCount,
          skippedCount: result.skippedCount,
          supplierGuid: supplierGuid,
        },
      };

      await firestore.collection(FIRESTORE_EXACT_HISTORY_TABLE).add(historyRecord);

      message.success(`Kostprijzen bijgewerkt: ${result.updatedCount} artikel(en) gesynchroniseerd naar Exact.`);
      setShowCostPriceModal(false);
      if (!costPriceRetryMode) handleOpenGoodsReceiptModal();
      else await loadCostPriceHistory();
    } catch (error) {
      console.error("Fout bij kostprijsupdate:", error);
      message.error(`Fout bij kostprijsupdate: ${error.message}`);
    } finally {
      setUpdatingCostPrices(false);
    }
  };

  const handleSkipCostPriceUpdate = () => {
    setShowCostPriceModal(false);
    if (!costPriceRetryMode) handleOpenGoodsReceiptModal();
  };

  const handleRollbackCostPrices = async () => {
    if (!costPriceHistory) return;
    try {
      setRollingBack(true);
      const idToken = await getFirebaseIDToken();

      const supplierGuid = costPriceHistory.metadata?.supplierGuid;
      if (!supplierGuid) {
        throw new Error("Geen leverancier GUID in history record");
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/purchase-orders/rollback-cost-prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          exchangeRate: 1,
          supplierGuid: supplierGuid,
          lines: costPriceHistory.oldValues.items.map((item) => ({
            itemCode: item.itemCode,
            unitPriceOriginal: 0,
            newCostPriceEur: item.costPriceStandard,
          })),
        }),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || "API fout bij rollback");
      }
      const result = await response.json();

      await firestore.collection(FIRESTORE_EXACT_HISTORY_TABLE).doc(costPriceHistory.id).update({
        canRollback: false,
        rolledBackAt: dayjs().unix(),
        rolledBackBy: email,
      });

      const rollbackHistoryRecord = {
        entityType: "item",
        entityId: "bulk_cost_price_rollback",
        operation: "cost_price_rollback",
        oldValues: costPriceHistory.newValues,
        newValues: costPriceHistory.oldValues,
        sourceType: "purchaseOrderShipment",
        sourceId: shipment.id,
        performedBy: email,
        performedAt: dayjs().unix(),
        canRollback: false,
        metadata: {
          shipmentNumber: shipment.shipmentNumber,
          originalHistoryId: costPriceHistory.id,
          updatedCount: result.updatedCount,
        },
      };
      await firestore.collection(FIRESTORE_EXACT_HISTORY_TABLE).add(rollbackHistoryRecord);

      message.success(`Kostprijzen teruggedraaid: ${result.updatedCount} artikel(en) hersteld.`);
      setCostPriceHistory(null);
    } catch (error) {
      console.error("Fout bij rollback:", error);
      message.error(`Fout bij terugdraaien: ${error.message}`);
    } finally {
      setRollingBack(false);
    }
  };

  const handleProcessStockCount = async () => {
    try {
      setProcessingReceipt(true);

      const lines = (shipment.items || []).map((item, index) => ({
        itemCode: item.productCode,
        orderedAmount: item.quantity,
        receivedAmount: receivedQuantities[index] || 0,
      }));

      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/exact/shipments/${id}/stock-count`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({
          receiptDate: receiptDate.format("DD-MM-YYYY"),
          lines,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "API fout");
      }

      const result = await response.json();
      const stockCountId = result.stockCountId || null;

      const oldLog = shipment.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "stock_count_processed",
        user: email,
        receiptDate: receiptDate.unix(),
        exactStockCountId: stockCountId,
      });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE)
        .doc(id)
        .update({
          status: "completed",
          receivedAt: receiptDate.unix(),
          receivedQuantities: lines.map((l) => ({
            productCode: l.itemCode,
            orderedAmount: l.orderedAmount,
            receivedAmount: l.receivedAmount,
          })),
          exactStockCountId: stockCountId,
          exactStockCountSyncedAt: dayjs().unix(),
          exactStockCountStatus: "synced",
          log: newLog,
        });

      // Stuur mail naar accountant en sla timestamp op
      let accountantMailSentAt = null;
      try {
        const mailResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/exact/shipments/${id}/notify-accountant`,
          { method: "POST", headers: { "Content-Type": "application/json", Authorization: idToken } },
        );
        if (mailResponse.ok) {
          accountantMailSentAt = dayjs().unix();
          await firestore.collection(FIRESTORE_PURCHASE_ORDER_SHIPMENTS_TABLE).doc(id).update({ accountantMailSentAt });
        }
      } catch (mailError) {
        console.warn("Could not send accountant mail:", mailError);
      }

      message.success("Voorraadtelling verwerkt! Voorraad bijgewerkt in Exact.");
      setShowGoodsReceiptModal(false);
      await loadShipment();
    } catch (error) {
      console.error("Error processing stock count:", error);
      message.error(`Fout bij verwerken voorraadtelling: ${error.message}`);
    } finally {
      setProcessingReceipt(false);
    }
  };

  const handleRetryExactSync = () => {
    const initialQuantities = {};
    if (shipment.receivedQuantities?.length) {
      shipment.receivedQuantities.forEach((rq) => {
        const itemIndex = (shipment.items || []).findIndex((item) => item.productCode === rq.productCode);
        if (itemIndex !== -1) initialQuantities[itemIndex] = rq.receivedAmount;
      });
    } else {
      (shipment.items || []).forEach((item, index) => {
        initialQuantities[index] = item.quantity;
      });
    }
    setReceivedQuantities(initialQuantities);
    setReceiptDate(shipment.receivedAt ? dayjs.unix(shipment.receivedAt) : dayjs());
    setGoodsReceiptIsRetry(true);
    setShowRetryExactModal(false);
    setShowGoodsReceiptModal(true);
  };

  const handleAddPayment = async () => {
    if (!invoice || !paymentAmount || paymentAmount <= 0) {
      message.error("Vul een geldig bedrag in");
      return;
    }
    if (!paymentExchangeRate || paymentExchangeRate <= 0) {
      message.error("Vul een geldige wisselkoers in");
      return;
    }
    if (!paymentDate) {
      message.error("Selecteer een betaaldatum");
      return;
    }

    try {
      setSavingPayment(true);

      let proofUrl = null;
      if (paymentProofFile) {
        const storage = getStorage();
        const fileName = `payments/${invoice.id}/${Date.now()}_${paymentProofFile.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, paymentProofFile);
        proofUrl = await getDownloadURL(storageRef);
      }

      const paymentData = {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        shipmentId: shipment.id,
        supplierId: shipment.supplierId,
        supplierName: shipment.supplierName || null,
        amount: paymentAmount,
        currency: shipmentCurrency,
        exchangeRate: paymentExchangeRate,
        amountEur: paymentAmount * paymentExchangeRate,
        paymentDate: paymentDate.unix(),
        reference: paymentReference.trim() || null,
        notes: paymentNotes.trim() || null,
        proofUrl,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add(paymentData);

      // Update invoice totals
      const newTotalPaid = (invoice.totalPaid || 0) + paymentAmount;
      const newAmountDue = (invoice.totalAmount || 0) - newTotalPaid;
      const newStatus =
        newTotalPaid >= (invoice.totalAmount || 0)
          ? "paid"
          : newTotalPaid > 0
            ? "partially_paid"
            : invoice.status || "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Betaling toegevoegd");
      setShowPaymentModal(false);
      resetPaymentForm();
      await loadShipment();
    } catch (error) {
      console.error("Error adding payment:", error);
      message.error("Fout bij toevoegen betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!shipment) return null;

  // Group items by source order
  const itemsByOrder = (shipment.items || []).reduce((acc, item) => {
    const key = item.purchaseOrderId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.purchaseOrderShipmentsList)}>
                {t("back")}
              </Button>
              <span>{shipment.shipmentNumber}</span>
              <Tag color={statusColors[shipment.status]}>{statusLabels[shipment.status]}</Tag>
            </Space>
            <Space>
              {shipment.status === "received" && (
                <Button type="primary" onClick={handleOpenCostPriceModal} loading={loadingCostPrices}>
                  Verwerk ontvangst
                </Button>
              )}
              {shipment.status === "completed" && !shipment.exactStockCountId && (
                <Button icon={<ReloadOutlined />} onClick={handleRetryExactSync}>
                  Synchroniseer naar Exact
                </Button>
              )}
              {shipment.status === "completed" && shipment.exactStockCountId && (
                <Button icon={<ReloadOutlined />} onClick={() => setShowRetryExactModal(true)}>
                  Exact opnieuw synchroniseren
                </Button>
              )}
              {shipment.status === "completed" && shipment.exactStockCountId && !shipment.accountantMailSentAt && (
                <Button icon={<MailOutlined />} onClick={handleResendAccountantMail} loading={sendingAccountantMail}>
                  Mail boekhouder versturen
                </Button>
              )}
              <Select
                value={shipment.status}
                style={{ minWidth: 180 }}
                onChange={handleUpdateStatus}
                loading={saving}
                options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
              />
            </Space>
          </div>
        }
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label={t("shipment.shipment") + " #"}>{shipment.shipmentNumber}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusColors[shipment.status]}>{statusLabels[shipment.status]}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("supplier.supplier")}>{shipment.supplierName || "-"}</Descriptions.Item>
          <Descriptions.Item label={t("total_products")}>{shipment.totalProducts}</Descriptions.Item>
          <Descriptions.Item label={t("total_price")}>
            {formatPriceByCurrency(shipment.totalPrice || 0, shipmentCurrency)}
          </Descriptions.Item>
          <Descriptions.Item label={t("tracking_code")}>
            {shipment.trackingCode ? (
              <a
                href={shipment.trackingCode.startsWith("http") ? shipment.trackingCode : undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {shipment.trackingCode}
              </a>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          {shipment.cubicMeters && (
            <Descriptions.Item label={t("cubic_meters")}>{shipment.cubicMeters} m³</Descriptions.Item>
          )}
          {shipment.etd && (
            <Descriptions.Item label={t("etd")}>{dayjs.unix(shipment.etd).format("DD-MM-YYYY")}</Descriptions.Item>
          )}
          {shipment.shippedAt && (
            <Descriptions.Item label="Verzonden op">
              {dayjs.unix(shipment.shippedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          {shipment.receivedAt && (
            <Descriptions.Item label={t("received_at")}>
              {dayjs.unix(shipment.receivedAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t("created_by")}>{shipment.createdBy}</Descriptions.Item>
          <Descriptions.Item label={t("date")}>
            {dayjs.unix(shipment.createdAt).format("DD-MM-YYYY HH:mm")}
          </Descriptions.Item>
          {shipment.accountantMailSentAt && (
            <Descriptions.Item label="Mail boekhouder verstuurd" span={2}>
              <span style={{ color: "#52c41a" }}>✓ </span>
              {dayjs.unix(shipment.accountantMailSentAt).format("DD-MM-YYYY HH:mm")}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t("container_number")} span={2}>
            {editingContainerNumber ? (
              <Space>
                <Input
                  value={containerNumberValue}
                  onChange={(e) => setContainerNumberValue(e.target.value)}
                  placeholder="bijv. MSCU1234567"
                  style={{ width: 220 }}
                  onPressEnter={handleSaveContainerNumber}
                />
                <Button type="primary" size="small" loading={savingContainerNumber} onClick={handleSaveContainerNumber}>
                  {t("save")}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEditingContainerNumber(false);
                    setContainerNumberValue(shipment.containerNumber || "");
                  }}
                >
                  {t("cancel")}
                </Button>
              </Space>
            ) : (
              <Space>
                <span>{shipment.containerNumber || <span style={{ color: "#bbb" }}>—</span>}</span>
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setEditingContainerNumber(true)}>
                  {t("edit")}
                </Button>
              </Space>
            )}
          </Descriptions.Item>
          {shipment.preOrderDatesUpdated && (
            <Descriptions.Item label="Pre-order datums" span={2}>
              <span style={{ color: "#52c41a" }}>✓ </span>
              Ingesteld op {shipment.preOrderAvailableAt}
              {shipment.preOrderDatesUpdatedAt && (
                <span style={{ color: "#888", marginLeft: 8 }}>
                  ({dayjs.unix(shipment.preOrderDatesUpdatedAt).format("DD-MM-YYYY HH:mm")})
                </span>
              )}
            </Descriptions.Item>
          )}
        </Descriptions>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
          <h4 style={{ margin: 0 }}>{t("order.purchase_orders")}</h4>
          {/*{costPriceHistory && (*/}
          {/*  <Button*/}
          {/*    danger*/}
          {/*    size="small"*/}
          {/*    icon={<ReloadOutlined />}*/}
          {/*    onClick={handleRollbackCostPrices}*/}
          {/*    loading={rollingBack}*/}
          {/*  >*/}
          {/*    Kostprijsupdate ongedaan maken*/}
          {/*  </Button>*/}
          {/*)}*/}
        </div>
        <Space wrap style={{ marginTop: 8 }}>
          {shipment.sourceOrders?.map((so) => (
            <Tag
              key={so.purchaseOrderId}
              color="blue"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`${urls.purchaseOrdersList}/${so.purchaseOrderId}`)}
            >
              {so.purchaseOrderNumber}
            </Tag>
          ))}
        </Space>
        {costPriceHistory && (
          <div style={{ marginTop: 8, padding: 8, backgroundColor: "#fff7e6", borderRadius: 4, fontSize: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                ⚠️{" "}
                <strong>
                  Kostprijzen bijgewerkt op {dayjs.unix(costPriceHistory.performedAt).format("DD-MM-YYYY HH:mm")}
                </strong>{" "}
                door {costPriceHistory.performedBy}. {costPriceHistory.metadata?.updatedCount} artikel(en) aangepast met
                wisselkoers {costPriceHistory.newValues.exchangeRate}.
              </div>
              {email.includes("digitalkiss") ||
                (email.includes("gideloff") && (
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => handleOpenCostPriceModal(true)}
                    loading={loadingCostPrices}
                  >
                    Opnieuw bijwerken
                  </Button>
                ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
          <h4 style={{ margin: 0 }}>{t("products")}</h4>
          <Space>
            {editingShipment && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleOpenAddShipmentItemModal}>
                Product toevoegen
              </Button>
            )}
            {!editingShipment && (
              <Button size="small" onClick={handleStartEditingShipment}>
                Aanpassen
              </Button>
            )}
            {editingShipment && (
              <Space>
                <Button size="small" onClick={handleCancelEditingShipment}>
                  Annuleren
                </Button>
                <Button size="small" type="primary" onClick={handleSaveShipmentEdits} loading={saving}>
                  Opslaan
                </Button>
              </Space>
            )}
          </Space>
        </div>

        <Table
          dataSource={
            editingShipment
              ? editedShipmentItems.map((item, idx) => ({ ...item, key: idx, _index: idx }))
              : (shipment.items || []).map((item, idx) => ({ ...item, key: idx }))
          }
          pagination={false}
          size="small"
          style={{ marginTop: 12 }}
          columns={[
            {
              title: t("product"),
              dataIndex: "productName",
              render: (name, record) => (
                <span>
                  {name}
                  {record.productColor && <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>}
                </span>
              ),
            },
            { title: "Code", dataIndex: "productCode", render: (v) => v || "-" },
            {
              title: t("quantity"),
              dataIndex: "quantity",
              render: (value, record) =>
                editingShipment ? (
                  <InputNumber
                    min={0}
                    value={value}
                    onChange={(v) => handleShipmentItemChange(record._index, "quantity", v || 0)}
                    style={{ width: 80 }}
                  />
                ) : (
                  value
                ),
            },
            {
              title: t("unit_price"),
              dataIndex: "unitPrice",
              render: (value, record) =>
                editingShipment ? (
                  <InputNumber
                    min={0}
                    step={0.01}
                    prefix={shipmentCurrency}
                    value={value}
                    onChange={(v) => handleShipmentItemChange(record._index, "unitPrice", v || 0)}
                    style={{ width: 100 }}
                  />
                ) : (
                  formatPriceByCurrency(value || 0, shipmentCurrency)
                ),
            },
            {
              title: t("total"),
              render: (_, record) =>
                formatPriceByCurrency((record.quantity || 0) * (record.unitPrice || 0), shipmentCurrency),
            },
            ...(editingShipment
              ? [
                  {
                    title: "Acties",
                    dataIndex: "actions",
                    width: 80,
                    render: (_, record) => (
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => handleRemoveShipmentItem(record._index)}
                        style={{ padding: 0 }}
                      >
                        Verwijderen
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />

        {/* Container grouping */}
        {shipment.containerNumber && (
          <Card
            size="small"
            style={{ marginTop: 24 }}
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  📦 Container: <strong>{shipment.containerNumber}</strong>
                </span>
                <Button type="primary" size="small" icon={<CheckOutlined />} onClick={handleOpenPreOrderModal}>
                  Pre-order datums instellen
                </Button>
              </div>
            }
          >
            {containerShipments.length === 0 ? (
              <div style={{ color: "#999", fontSize: 13 }}>Geen andere verzendingen in deze container</div>
            ) : (
              <>
                <div style={{ marginBottom: 8, fontSize: 13, color: "#555" }}>
                  {containerShipments.length} andere verzending(en) in dezelfde container:
                </div>
                {containerShipments.map((cs) => (
                  <Card
                    key={cs.id}
                    size="small"
                    style={{ marginBottom: 8 }}
                    title={
                      <Space>
                        <Tag
                          color="blue"
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate(`${urls.purchaseOrderShipmentsList}/${cs.id}`)}
                        >
                          {cs.shipmentNumber}
                        </Tag>
                        <Tag color={statusColors[cs.status]}>{statusLabels[cs.status]}</Tag>
                        {cs.supplierName && <span style={{ color: "#666", fontSize: 12 }}>{cs.supplierName}</span>}
                        {cs.preOrderDatesUpdated && <Tag color="green">Pre-order: {cs.preOrderAvailableAt}</Tag>}
                      </Space>
                    }
                  >
                    <Space wrap>
                      {(cs.items || []).map((item, idx) => (
                        <Tag key={idx}>
                          {item.productName}
                          {item.productColor ? ` (${item.productColor})` : ""}
                        </Tag>
                      ))}
                    </Space>
                  </Card>
                ))}
              </>
            )}
          </Card>
        )}

        {/* Invoice */}
        {invoice && (
          <Card
            size="small"
            style={{ marginTop: 24, backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#389e0d", fontWeight: "bold" }}>📋 Factuur — {invoice.invoiceNumber}</span>
                <Space>
                  <Tag color={invoiceStatusColors[invoice.status] || "default"}>
                    {invoiceStatusLabels[invoice.status] || invoice.status || "Ingediend"}
                  </Tag>
                  <Select
                    size="small"
                    value={invoice.status || "submitted"}
                    style={{ minWidth: 140 }}
                    onChange={handleUpdateInvoiceStatus}
                    loading={saving}
                    options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
                  />
                </Space>
              </div>
            }
          >
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Factuurnummer">{invoice.invoiceNumber}</Descriptions.Item>
              <Descriptions.Item label="Factuurdatum">
                {invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Subtotaal producten">
                {formatPriceByCurrency(invoice.subtotalProducts || 0, shipmentCurrency)}
              </Descriptions.Item>
              <Descriptions.Item label="Bijkomende kosten">
                {formatPriceByCurrency(invoice.subtotalAdditionalCosts || 0, shipmentCurrency)}
              </Descriptions.Item>
              <Descriptions.Item label="Totaalbedrag">
                <span style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
                  {formatPriceByCurrency(invoice.totalAmount || 0, shipmentCurrency)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Betaald / Openstaand">
                <span style={{ fontWeight: "bold", color: "#1890ff" }}>
                  {formatPriceByCurrency(invoice.totalPaid || 0, shipmentCurrency)}
                </span>
                {" / "}
                <span style={{ fontWeight: "bold", color: (invoice.amountDue || 0) > 0 ? "#ff4d4f" : "#52c41a" }}>
                  {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, shipmentCurrency)}
                </span>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
              <h5 style={{ margin: 0 }}>Factuurregels</h5>
              <Space>
                {editingInvoice && (
                  <Button size="small" icon={<PlusOutlined />} onClick={handleOpenAddLineModal}>
                    Regel toevoegen
                  </Button>
                )}
                {!editingInvoice && (
                  <Button size="small" onClick={handleStartEditingInvoice}>
                    Factuur aanpassen
                  </Button>
                )}
                {editingInvoice && (
                  <Space>
                    <Button size="small" onClick={handleCancelEditingInvoice}>
                      Annuleren
                    </Button>
                    <Button size="small" type="primary" onClick={handleSaveInvoiceEdits} loading={saving}>
                      Opslaan
                    </Button>
                  </Space>
                )}
              </Space>
            </div>

            {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length > 0 && (
              <Table
                dataSource={(editingInvoice ? editedInvoiceLines : invoice.lines || []).map((l, i) => ({
                  ...l,
                  key: i,
                  _index: i,
                }))}
                pagination={false}
                size="small"
                style={{ marginTop: 12, marginBottom: 16 }}
                columns={[
                  {
                    title: t("product"),
                    dataIndex: "productName",
                    render: (name, record) => (
                      <span>
                        {name}
                        {record.productColor && (
                          <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                        )}
                      </span>
                    ),
                  },
                  {
                    title: t("quantity"),
                    dataIndex: "quantity",
                    render: (value, record) =>
                      editingInvoice ? (
                        <InputNumber
                          min={0}
                          value={value}
                          onChange={(v) => handleInvoiceLineChange(record._index, "quantity", v || 0)}
                          style={{ width: 80 }}
                        />
                      ) : (
                        value
                      ),
                  },
                  {
                    title: t("unit_price"),
                    dataIndex: "unitPrice",
                    render: (value, record) =>
                      editingInvoice ? (
                        <InputNumber
                          min={0}
                          step={0.01}
                          prefix={shipmentCurrency}
                          value={value}
                          onChange={(v) => handleInvoiceLineChange(record._index, "unitPrice", v || 0)}
                          style={{ width: 100 }}
                        />
                      ) : (
                        formatPriceByCurrency(value || 0, shipmentCurrency)
                      ),
                  },
                  {
                    title: "Korting",
                    dataIndex: "discountPercentage",
                    render: (value, record) =>
                      editingInvoice ? (
                        <InputNumber
                          min={0}
                          max={100}
                          value={value}
                          onChange={(v) => handleInvoiceLineChange(record._index, "discountPercentage", v || 0)}
                          style={{ width: 70 }}
                          suffix="%"
                        />
                      ) : value ? (
                        `${value}%`
                      ) : (
                        "-"
                      ),
                  },
                  {
                    title: t("total"),
                    dataIndex: "totalPrice",
                    render: (v) => formatPriceByCurrency(v || 0, shipmentCurrency),
                  },
                  ...(editingInvoice
                    ? [
                        {
                          title: "Acties",
                          dataIndex: "actions",
                          width: 80,
                          render: (_, record) => (
                            <Button
                              size="small"
                              type="link"
                              danger
                              onClick={() => handleRemoveInvoiceLine(record._index)}
                              style={{ padding: 0 }}
                            >
                              Verwijderen
                            </Button>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            )}

            {(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).length > 0 && (
              <>
                <h5 style={{ marginTop: 16 }}>Bijkomende kosten</h5>
                <Table
                  dataSource={(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).map((c, i) => ({
                    ...c,
                    key: i,
                    _index: i,
                  }))}
                  pagination={false}
                  size="small"
                  style={{ marginBottom: 16 }}
                  columns={[
                    { title: "Omschrijving", dataIndex: "description" },
                    { title: t("quantity"), dataIndex: "quantity", render: (v) => v || 1 },
                    {
                      title: t("unit_price"),
                      dataIndex: "unitPrice",
                      render: (v) => (v ? formatPriceByCurrency(v, shipmentCurrency) : "-"),
                    },
                    {
                      title: t("total"),
                      dataIndex: "amount",
                      render: (value, record) =>
                        editingInvoice ? (
                          <InputNumber
                            min={0}
                            step={0.01}
                            prefix={shipmentCurrency}
                            value={value}
                            onChange={(v) => handleAdditionalCostChange(record._index, "amount", v || 0)}
                            style={{ width: 100 }}
                          />
                        ) : (
                          formatPriceByCurrency(value || 0, shipmentCurrency)
                        ),
                    },
                  ]}
                />
              </>
            )}

            {invoice.pdfUrl && (
              <div style={{ marginTop: 8 }}>
                <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="primary" size="small">
                    Download factuur PDF
                  </Button>
                </a>
              </div>
            )}

            {invoice.remarks && (
              <div style={{ marginTop: 12, padding: 8, backgroundColor: "#fff", borderRadius: 4 }}>
                <strong>Opmerkingen:</strong>
                <div style={{ marginTop: 4, color: "#666" }}>{invoice.remarks}</div>
              </div>
            )}

            <Space style={{ marginTop: 12 }}>
              <Tag color="green">✓ Factuur ontvangen</Tag>
              {invoice.exactPurchaseInvoiceId && <Tag color="blue">✓ Gesynchroniseerd met Exact</Tag>}
            </Space>

            {/* Payments Section */}
            <div style={{ marginTop: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h5 style={{ margin: 0 }}>💰 Betalingen</h5>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={() => {
                    resetPaymentForm();
                    setPaymentAmount(invoice.amountDue > 0 ? invoice.amountDue : null);
                    setShowPaymentModal(true);
                  }}
                >
                  Betaling toevoegen
                </Button>
              </div>

              {allPayments.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    backgroundColor: "#fafafa",
                    borderRadius: 8,
                    textAlign: "center",
                    color: "#999",
                  }}
                >
                  Nog geen betalingen geregistreerd
                </div>
              ) : (
                <Table
                  dataSource={allPayments.map((p, i) => ({ ...p, key: i }))}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: "Datum",
                      dataIndex: "paymentDate",
                      render: (v, record) => {
                        const date = v || record.paidAt || record.date;
                        return date ? dayjs.unix(date).format("DD-MM-YYYY") : "-";
                      },
                    },
                    {
                      title: "Bedrag",
                      dataIndex: "amount",
                      render: (v, record) => (
                        <div>
                          <strong style={{ color: "#52c41a" }}>
                            {formatPriceByCurrency(v || 0, shipmentCurrency)}
                          </strong>
                          {record.exchangeRate && (
                            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                              €{(v * record.exchangeRate).toFixed(2)} @ {record.exchangeRate}
                            </div>
                          )}
                          {record.isLegacy && (
                            <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                              Aanbetaling via {record.sourceOrderNumber}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: "Referentie",
                      dataIndex: "reference",
                      render: (v, record) => {
                        if (record.isLegacy) {
                          return record.sourceOrderNumber || "-";
                        }
                        return v || "-";
                      },
                    },
                    {
                      title: "Notities",
                      dataIndex: "notes",
                      render: (v, record) => {
                        const notes = v || record.notes || "";
                        if (record.isLegacy && !notes) {
                          return "Aanbetaling via inkooporder";
                        }
                        return notes || "-";
                      },
                    },
                    {
                      title: "Bewijs",
                      dataIndex: "proofUrl",
                      render: (v, record) => {
                        const proofUrl = v || record.proofUrl;
                        return proofUrl ? (
                          <a href={proofUrl} target="_blank" rel="noopener noreferrer">
                            <Button size="small" type="link" style={{ padding: 0 }}>
                              Bekijk
                            </Button>
                          </a>
                        ) : (
                          "-"
                        );
                      },
                    },
                    {
                      title: "Door",
                      dataIndex: "createdBy",
                      render: (v, record) => {
                        const createdBy = v || record.createdBy || record.paidBy;
                        return <span style={{ fontSize: 11, color: "#888" }}>{createdBy || "-"}</span>;
                      },
                    },
                    {
                      title: "Acties",
                      dataIndex: "actions",
                      render: (_, record) => (
                        <Space size="small">
                          {!record.isLegacy && (
                            <Button
                              size="small"
                              type="link"
                              onClick={() => handleEditPayment(record)}
                              style={{ padding: 0 }}
                            >
                              Bewerken
                            </Button>
                          )}
                          {record.isLegacy && <span style={{ fontSize: 11, color: "#999" }}>Legacy</span>}
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          </Card>
        )}

        {/* Retry Exact Sync Modal */}
        <Modal
          title={
            <Space>
              <WarningOutlined style={{ color: shipment.exactGoodsReceiptSyncedAt ? "#faad14" : "#1890ff" }} />
              <span>
                {shipment.exactGoodsReceiptSyncedAt ? "Exact opnieuw synchroniseren" : "Synchroniseer naar Exact"}
              </span>
            </Space>
          }
          open={showRetryExactModal}
          onOk={handleRetryExactSync}
          onCancel={() => setShowRetryExactModal(false)}
          okText="Ja, synchroniseer opnieuw"
          cancelText="Annuleren"
          confirmLoading={retryingExactSync}
          okButtonProps={{ danger: !!shipment.exactGoodsReceiptSyncedAt }}
        >
          {shipment.exactGoodsReceiptSyncedAt ? (
            <>
              <p>
                <strong>
                  Deze verzending is al gesynchroniseerd met Exact op{" "}
                  {dayjs.unix(shipment.exactGoodsReceiptSyncedAt).format("DD-MM-YYYY HH:mm")}.
                </strong>
              </p>
              <p style={{ color: "#ff4d4f" }}>
                ⚠️ Als je opnieuw synchroniseert, wordt de voorraadverhoging opnieuw uitgevoerd in Exact. Dit kan leiden
                tot een dubbele voorraadverhoging.
              </p>
              <p>
                Doe dit alleen als de vorige synchronisatie aantoonbaar mislukt is en de voorraad NIET is bijgewerkt in
                Exact.
              </p>
              {shipment.exactGoodsReceiptStatus === "failed" && shipment.exactGoodsReceiptError && (
                <div style={{ padding: 8, backgroundColor: "#fff1f0", borderRadius: 4, marginTop: 8 }}>
                  <strong>Vorige foutmelding:</strong>
                  <div style={{ fontSize: 12, color: "#cf1322", marginTop: 4 }}>{shipment.exactGoodsReceiptError}</div>
                </div>
              )}
            </>
          ) : (
            <p>
              De Exact synchronisatie is nog niet uitgevoerd voor deze verzending. Klik op OK om de goods receipt te
              sturen naar Exact.
            </p>
          )}
        </Modal>

        {/* Cost Price Update Modal */}
        <Modal
          title={
            costPriceRetryMode ? "💰 Kostprijzen opnieuw bijwerken" : "💰 Stap 1/2 — Kostprijzen bijwerken in Exact"
          }
          open={showCostPriceModal}
          onOk={
            costPricesAlreadyUpdated && !costPriceRetryMode ? handleSkipCostPriceUpdate : handleConfirmCostPriceUpdate
          }
          onCancel={handleSkipCostPriceUpdate}
          okText={
            costPricesAlreadyUpdated && !costPriceRetryMode ? (
              <>
                <CheckOutlined /> Doorgaan naar telling
              </>
            ) : (
              <>
                <CheckOutlined /> Prijzen bijwerken{costPriceRetryMode ? "" : " & doorgaan"}
              </>
            )
          }
          cancelText={costPriceRetryMode ? "Annuleren" : "Overslaan (alleen tellen)"}
          confirmLoading={updatingCostPrices}
          okButtonProps={{
            disabled: costPricesAlreadyUpdated && !costPriceRetryMode,
          }}
          width={850}
        >
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>
                Controleer de nieuwe kostprijzen voordat je de ontvangst verwerkt.
              </p>
              <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#666" }}>
                Formule: inkoopprijs &times; wisselkoers = kostprijs in EUR. Na bevestiging wordt{" "}
                <strong>CostPriceStandard</strong> per artikel bijgewerkt in Exact Online.
              </p>
            </div>

            <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <label style={{ fontWeight: 500, whiteSpace: "nowrap" }}>Wisselkoers (gewogen gemiddelde):</label>
              <InputNumber
                min={0.0001}
                step={0.001}
                precision={4}
                value={costPriceExchangeRate}
                onChange={handleExchangeRateChange}
                style={{ width: 140 }}
              />
              <span style={{ fontSize: 12, color: "#888" }}>Gewogen op basis van alle betalingen</span>
            </div>

            {/* Weighted Exchange Rate Overview */}
            {allPayments.length > 0 && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#f6ffed", borderRadius: 8 }}>
                {(() => {
                  // Calculate current weighted rate
                  let totalAmount = 0;
                  let weightedRateSum = 0;
                  const relevantPayments = allPayments.filter((p) => p.exchangeRate);

                  for (const payment of relevantPayments) {
                    const amount = Number(payment.amount) || 0;
                    const rate = Number(payment.exchangeRate) || 1;
                    totalAmount += amount;
                    weightedRateSum += amount * rate;
                  }

                  const currentWeightedRate = totalAmount > 0 ? weightedRateSum / totalAmount : 0;

                  return (
                    <div>
                      <div style={{ fontSize: 12, marginBottom: 6 }}>
                        <strong>Berekening:</strong>
                      </div>
                      {relevantPayments.map((payment, index) => (
                        <div key={index} style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>
                          - {formatPriceByCurrency(payment.amount, shipmentCurrency)} × {payment.exchangeRate} = €
                          {(payment.amount * payment.exchangeRate).toFixed(2)}
                          {payment.isLegacy && ` (aanbetaling ${payment.sourceOrderNumber})`}
                        </div>
                      ))}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#666",
                          marginTop: 4,
                          paddingTop: 4,
                          borderTop: "1px solid #d9f7be",
                        }}
                      >
                        <strong>
                          Totaal: {formatPriceByCurrency(totalAmount, shipmentCurrency)} → €{weightedRateSum.toFixed(2)}
                        </strong>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#52c41a", marginTop: 4 }}>
                        Gewogen koers: {currentWeightedRate.toFixed(4)}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <Table
              dataSource={costPriceLines.map((line, i) => ({ ...line, key: i, _index: i }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Product",
                  dataIndex: "productName",
                  render: (name, record) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{name}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>{record.itemCode}</div>
                    </div>
                  ),
                },
                {
                  title: "Inkoopprijs",
                  dataIndex: "unitPriceOriginal",
                  width: 120,
                  render: (v) => formatPriceByCurrency(v || 0, shipmentCurrency),
                },
                {
                  title: "Wisselkoers",
                  dataIndex: "_index",
                  width: 130,
                  render: (idx) => (
                    <InputNumber
                      min={0.0001}
                      step={0.001}
                      precision={4}
                      value={costPriceLines[idx]?.exchangeRate ?? costPriceExchangeRate}
                      onChange={(v) => handleCostPriceLineChange(idx, "exchangeRate", v || 1)}
                      style={{ width: "100%" }}
                    />
                  ),
                },
                {
                  title: "Nieuwe kostprijs (EUR)",
                  dataIndex: "_index",
                  width: 165,
                  render: (idx) => (
                    <InputNumber
                      min={0}
                      step={0.01}
                      precision={2}
                      prefix="€"
                      value={costPriceLines[idx]?.newCostPriceEur ?? 0}
                      onChange={(v) => handleCostPriceLineChange(idx, "newCostPriceEur", v || 0)}
                      style={{ width: "100%" }}
                    />
                  ),
                },
              ]}
            />

            <div style={{ marginTop: 16, padding: 12, backgroundColor: "#fff7e6", borderRadius: 8 }}>
              <p style={{ margin: 0, fontSize: 12, color: "#d48806" }}>
                <strong>Overslaan:</strong> klik op <em>"Overslaan"</em> om de kostprijzen niet te wijzigen en direct
                door te gaan naar het tellen van de goederen.
              </p>
            </div>
          </div>
        </Modal>

        {/* Goods Receipt Modal - Step 2 */}
        <Modal
          title="📦 Verwerk Ontvangst - Tellingen Invoeren"
          open={showGoodsReceiptModal}
          onCancel={() => setShowGoodsReceiptModal(false)}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => setShowGoodsReceiptModal(false)} disabled={processingReceipt}>
              Annuleren
            </Button>,
            <Button
              key="stock-count"
              type={"primary"}
              onClick={
                shipment.exactStockCountId && !goodsReceiptIsRetry
                  ? () => setShowGoodsReceiptModal(false)
                  : handleProcessStockCount
              }
              loading={processingReceipt}
            >
              {shipment.exactStockCountId && !goodsReceiptIsRetry ? "Sluiten" : "🔢 Voorraadtelling verwerken"}
            </Button>,
          ]}
        >
          <div style={{ marginTop: 16 }}>
            {shipment.exactStockCountId && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#fff7e6", borderRadius: 8 }}>
                <p style={{ margin: 0, fontWeight: 500, color: "#d48806" }}>
                  ⚠️ <strong>Let op:</strong> Deze verzending is al verwerkt in Exact Online.
                </p>
                <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#d48806" }}>
                  Exact Stock Count ID: {shipment.exactStockCountId}
                  {shipment.exactStockCountSyncedAt && (
                    <span>
                      {" "}
                      • Verwerkt op: {dayjs.unix(shipment.exactStockCountSyncedAt).format("DD-MM-YYYY HH:mm")}
                    </span>
                  )}
                </p>
              </div>
            )}
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>ℹ️ Vul hieronder de getelde aantallen in per product.</p>
              <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#666" }}>
                Na verwerking wordt de voorraad automatisch bijgewerkt in Exact Online en ontvangt de boekhouder een
                email.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Ontvangstdatum <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <DatePicker style={{ width: "100%" }} value={receiptDate} onChange={setReceiptDate} format="DD-MM-YYYY" />
            </div>

            <h5 style={{ marginBottom: 12 }}>Producten</h5>
            <Table
              dataSource={(shipment.items || []).map((item, index) => ({ ...item, index, key: index }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Product",
                  dataIndex: "productName",
                  render: (name, record) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{name}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>Code: {record.productCode || "-"}</div>
                    </div>
                  ),
                },
                {
                  title: "Verzonden",
                  dataIndex: "quantity",
                  width: 100,
                  render: (qty) => <span style={{ fontWeight: 500 }}>{qty}</span>,
                },
                {
                  title: "Geteld Aantal",
                  dataIndex: "index",
                  width: 150,
                  render: (index, record) => (
                    <InputNumber
                      min={0}
                      value={receivedQuantities[index]}
                      onChange={(value) => setReceivedQuantities((prev) => ({ ...prev, [index]: value }))}
                      style={{ width: "100%" }}
                      status={receivedQuantities[index] !== record.quantity ? "warning" : undefined}
                    />
                  ),
                },
                {
                  title: "Verschil",
                  dataIndex: "index",
                  width: 100,
                  render: (index, record) => {
                    const diff = (receivedQuantities[index] || 0) - record.quantity;
                    return (
                      <span
                        style={{ color: diff === 0 ? "#52c41a" : diff < 0 ? "#ff4d4f" : "#faad14", fontWeight: 500 }}
                      >
                        {diff > 0 ? "+" : ""}
                        {diff}
                      </span>
                    );
                  },
                },
              ]}
            />

            {!shipment.exactStockCountId && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: "#fff7e6", borderRadius: 8 }}>
                <p style={{ margin: 0, fontSize: 12, color: "#d48806" }}>
                  ⚠️ <strong>Let op:</strong> Deze actie kan niet ongedaan worden gemaakt. De voorraad wordt direct
                  bijgewerkt in Exact Online.
                </p>
              </div>
            )}
          </div>
        </Modal>

        {/* Add Shipment Item Modal */}
        <Modal
          title="Product toevoegen aan zending"
          open={showAddShipmentItemModal}
          onOk={handleAddShipmentItem}
          onCancel={handleCloseAddShipmentItemModal}
          okText="Toevoegen"
          cancelText="Annuleren"
          width={500}
        >
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Product <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder={
                  products.length === 0 ? "Geen producten beschikbaar voor deze leverancier" : "Selecteer een product"
                }
                value={selectedShipmentProduct?.id}
                onChange={handleShipmentProductSelect}
                showSearch
                filterOption={(input, option) => {
                  const product = products.find((p) => p.id === option.value);
                  if (!product) return false;
                  const searchText = input.toLowerCase();
                  return (
                    product.name.toLowerCase().includes(searchText) ||
                    (product.code && product.code.toLowerCase().includes(searchText))
                  );
                }}
                disabled={products.length === 0}
              >
                {products
                  .filter((product) => {
                    const alreadyInItems = editedShipmentItems.some(
                      (item) =>
                        item.productId === product.id || (item.productCode && item.productCode === product.code),
                    );
                    return !alreadyInItems;
                  })
                  .map((product) => (
                    <Select.Option key={product.id} value={product.id}>
                      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 500 }}>{product.name}</span>
                        <span style={{ fontSize: 11, color: "#888" }}>{product.code || ""}</span>
                      </div>
                    </Select.Option>
                  ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Hoeveelheid <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                value={newShipmentItemQuantity}
                onChange={setNewShipmentItemQuantity}
                placeholder="1"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Inkoopprijs ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                prefix={shipmentCurrency}
                value={newShipmentItemUnitPrice}
                onChange={setNewShipmentItemUnitPrice}
                placeholder="0.00"
              />
            </div>

            {newShipmentItemQuantity > 0 && newShipmentItemUnitPrice > 0 && (
              <div style={{ padding: 12, backgroundColor: "#f6ffed", borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Totaal:</div>
                <div style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
                  {formatPriceByCurrency(newShipmentItemQuantity * newShipmentItemUnitPrice, shipmentCurrency)}
                </div>
              </div>
            )}
          </div>
        </Modal>

        {/* Add Invoice Line Modal */}
        <Modal
          title="Factuurregel toevoegen"
          open={showAddLineModal}
          onOk={handleAddInvoiceLine}
          onCancel={handleCloseAddLineModal}
          okText="Toevoegen"
          cancelText="Annuleren"
          width={600}
        >
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Product <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder={
                  products.length === 0 ? "Geen producten beschikbaar voor deze leverancier" : "Selecteer een product"
                }
                value={selectedProduct?.id}
                onChange={handleProductSelect}
                showSearch
                filterOption={(input, option) => {
                  const product = products.find((p) => p.id === option.value);
                  if (!product) return false;
                  const searchText = input.toLowerCase();
                  return (
                    product.name.toLowerCase().includes(searchText) ||
                    (product.sku && product.sku.toLowerCase().includes(searchText)) ||
                    (product.code && product.code.toLowerCase().includes(searchText))
                  );
                }}
                disabled={products.length === 0}
              >
                {products
                  .filter((product) => {
                    const alreadyInLines = editedInvoiceLines.some(
                      (line) =>
                        line.productId === product.id ||
                        (line.productCode && line.productCode === (product.sku || product.code)),
                    );
                    return !alreadyInLines;
                  })
                  .map((product) => (
                    <Select.Option key={product.id} value={product.id}>
                      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 500 }}>{product.name}</span>
                        <span style={{ fontSize: 11, color: "#888" }}>{product.code || ""}</span>
                      </div>
                    </Select.Option>
                  ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Hoeveelheid <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                value={newLineQuantity}
                onChange={setNewLineQuantity}
                placeholder="1"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Eenheidsprijs ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                prefix={shipmentCurrency}
                value={newLineUnitPrice}
                onChange={setNewLineUnitPrice}
                placeholder="0.00"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Korting (%)</label>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                max={100}
                value={newLineDiscount}
                onChange={setNewLineDiscount}
                placeholder="0"
                suffix="%"
              />
            </div>

            {newLineQuantity && newLineUnitPrice && (
              <div style={{ padding: 12, backgroundColor: "#f6ffed", borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>Totaal (excl. korting):</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {formatPriceByCurrency(newLineQuantity * newLineUnitPrice, shipmentCurrency)}
                </div>
                {newLineDiscount > 0 && (
                  <>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 4, marginBottom: 4 }}>Korting:</div>
                    <div style={{ fontSize: 14, color: "#ff4d4f" }}>
                      -
                      {formatPriceByCurrency(
                        newLineQuantity * newLineUnitPrice * (newLineDiscount / 100),
                        shipmentCurrency,
                      )}
                    </div>
                  </>
                )}
                <div style={{ fontSize: 12, color: "#666", marginTop: 8, marginBottom: 4 }}>
                  Totaal (incl. korting):
                </div>
                <div style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
                  {formatPriceByCurrency(
                    newLineQuantity * newLineUnitPrice * (1 - newLineDiscount / 100),
                    shipmentCurrency,
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>

        {/* Payment Modal */}
        <Modal
          title="Betaling toevoegen"
          open={showPaymentModal}
          onOk={handleAddPayment}
          onCancel={() => {
            setShowPaymentModal(false);
            resetPaymentForm();
          }}
          okText="Betaling opslaan"
          confirmLoading={savingPayment}
        >
          <div style={{ marginTop: 16 }}>
            {invoice && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
                <div>
                  Factuur: <strong>{invoice.invoiceNumber}</strong>
                </div>
                <div>
                  Totaal: <strong>{formatPriceByCurrency(invoice.totalAmount || 0, shipmentCurrency)}</strong>
                </div>
                <div>
                  Reeds betaald: <strong>{formatPriceByCurrency(invoice.totalPaid || 0, shipmentCurrency)}</strong>
                </div>
                <div>
                  Openstaand:{" "}
                  <strong style={{ color: "#ff4d4f" }}>
                    {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, shipmentCurrency)}
                  </strong>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Bedrag ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                value={paymentAmount}
                onChange={setPaymentAmount}
                placeholder="0.00"
              />
              {paymentAmount && paymentExchangeRate && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>
                  ≈ €{(paymentAmount * paymentExchangeRate).toFixed(2)}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Wisselkoers ({shipmentCurrency} → EUR) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.0001}
                step={0.0001}
                precision={4}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                placeholder={shipmentCurrency === "IDR" ? "bijv. 0.00006" : "bijv. 0.8500"}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>Conversie naar euro voor administratie</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Betaaldatum <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <DatePicker style={{ width: "100%" }} value={paymentDate} onChange={setPaymentDate} format="DD-MM-YYYY" />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Referentie / Transactie ID</label>
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="bijv. TXN-123456"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Notities</label>
              <TextArea
                rows={2}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Optionele opmerkingen"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Betalingsbewijs (PDF/afbeelding)
              </label>
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                maxCount={1}
                beforeUpload={(file) => {
                  if (file.size > 10 * 1024 * 1024) {
                    message.error("Bestand moet kleiner zijn dan 10MB");
                    return Upload.LIST_IGNORE;
                  }
                  setPaymentProofFile(file);
                  return false;
                }}
                onRemove={() => setPaymentProofFile(null)}
                fileList={
                  paymentProofFile
                    ? [{ uid: "-1", name: paymentProofFile.name, status: "done", size: paymentProofFile.size }]
                    : []
                }
              >
                <Button icon={<UploadOutlined />}>Selecteer bestand</Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Edit Payment Modal */}
        <Modal
          title="Betaling bewerken"
          open={showEditPaymentModal}
          onOk={handleUpdatePayment}
          onCancel={() => {
            setShowEditPaymentModal(false);
            setEditingPayment(null);
            resetPaymentForm();
          }}
          okText="Wijzigingen opslaan"
          confirmLoading={savingPayment}
        >
          <div style={{ marginTop: 16 }}>
            {editingPayment && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
                <div>
                  Factuur: <strong>{invoice?.invoiceNumber}</strong>
                </div>
                <div>
                  Huidig bedrag: <strong>{formatPriceByCurrency(editingPayment.amount || 0, shipmentCurrency)}</strong>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Bedrag ({shipmentCurrency}) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                value={paymentAmount}
                onChange={setPaymentAmount}
                placeholder="0.00"
              />
              {paymentAmount && paymentExchangeRate && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>
                  ≈ €{(paymentAmount * paymentExchangeRate).toFixed(2)}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Wisselkoers ({shipmentCurrency} → EUR) <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0.0001}
                step={0.0001}
                precision={4}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                placeholder={shipmentCurrency === "IDR" ? "bijv. 0.00006" : "bijv. 0.8500"}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>Conversie naar euro voor administratie</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Betaaldatum <span style={{ color: "#ff4d4f" }}>*</span>
              </label>
              <DatePicker style={{ width: "100%" }} value={paymentDate} onChange={setPaymentDate} format="DD-MM-YYYY" />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Referentie / Transactie ID</label>
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="bijv. TXN-123456"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Notities</label>
              <TextArea
                rows={2}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Optionele opmerkingen"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Betalingsbewijs (PDF/afbeelding)
              </label>
              {editingPayment?.proofUrl && (
                <div style={{ marginBottom: 8 }}>
                  <a href={editingPayment.proofUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="small" type="link" style={{ padding: 0 }}>
                      Huidig bewijs bekijken
                    </Button>
                  </a>
                </div>
              )}
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                maxCount={1}
                beforeUpload={(file) => {
                  if (file.size > 10 * 1024 * 1024) {
                    message.error("Bestand moet kleiner zijn dan 10MB");
                    return Upload.LIST_IGNORE;
                  }
                  setPaymentProofFile(file);
                  return false;
                }}
                onRemove={() => setPaymentProofFile(null)}
                fileList={
                  paymentProofFile
                    ? [{ uid: "-1", name: paymentProofFile.name, status: "done", size: paymentProofFile.size }]
                    : []
                }
              >
                <Button icon={<UploadOutlined />}>
                  {editingPayment?.proofUrl ? "Vervang bewijs" : "Selecteer bestand"}
                </Button>
              </Upload>
            </div>
          </div>
        </Modal>

        {/* Pre-order Modal */}
        <Modal
          title="Pre-order datums instellen"
          open={showPreOrderModal}
          onOk={handleSavePreOrder}
          onCancel={() => setShowPreOrderModal(false)}
          okText="Datums instellen"
          confirmLoading={savingPreOrder}
        >
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: "#e6f7ff", borderRadius: 8 }}>
            <div>
              Container: <strong>{shipment.containerNumber}</strong>
            </div>
            <div style={{ marginTop: 4, fontSize: 13, color: "#555" }}>
              Alle producten in de geselecteerde verzendingen worden op pre-order gezet met de gekozen datum als
              beschikbaarheidsdatum.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
              Aankomstdatum <span style={{ color: "#ff4d4f" }}>*</span>
            </label>
            <DatePicker
              style={{ width: "100%" }}
              value={preOrderDate}
              onChange={setPreOrderDate}
              format="DD-MM-YYYY"
              placeholder="Selecteer aankomstdatum"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </div>

          <div style={{ marginBottom: 8, fontWeight: 500 }}>Verzendingen in deze container:</div>
          {[
            { id, shipmentNumber: shipment.shipmentNumber, supplierName: shipment.supplierName, items: shipment.items },
            ...containerShipments,
          ].map((cs) => (
            <div
              key={cs.id}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 8,
                padding: "6px 10px",
                backgroundColor: "#fafafa",
                borderRadius: 6,
              }}
            >
              <input
                type="checkbox"
                style={{ marginRight: 10 }}
                checked={preOrderShipmentIds.includes(cs.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setPreOrderShipmentIds((prev) => [...prev, cs.id]);
                  } else {
                    setPreOrderShipmentIds((prev) => prev.filter((sid) => sid !== cs.id));
                  }
                }}
              />
              <div>
                <strong>{cs.shipmentNumber}</strong>
                {cs.supplierName && (
                  <span style={{ color: "#666", marginLeft: 6, fontSize: 12 }}>{cs.supplierName}</span>
                )}
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                  {[...new Set((cs.items || []).map((i) => i.productName))].join(", ")}
                </div>
              </div>
            </div>
          ))}
        </Modal>

        {/* Activity Log */}
        {shipment.log && shipment.log.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Collapse>
              <Panel header={t("activity_log")} key="1">
                <div style={{ maxHeight: 400, overflowY: "auto", paddingTop: 20 }}>
                  <Timeline>
                    {[...shipment.log].reverse().map((entry, idx) => (
                      <Timeline.Item key={idx}>
                        <div>
                          <p style={{ margin: 0 }}>
                            <strong>{entry.action}</strong>
                          </p>
                          <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                            {entry.user} - {dayjs.unix(entry.timestamp).format("DD-MM-YYYY HH:mm")}
                          </p>
                          {entry.oldValue && entry.newValue && (
                            <p style={{ margin: 0, fontSize: 12 }}>
                              {statusLabels[entry.oldValue] || entry.oldValue} →{" "}
                              {statusLabels[entry.newValue] || entry.newValue}
                            </p>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              </Panel>
            </Collapse>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PurchaseOrderShipmentDetail;
